import { Checkbox, FormControlLabel, Stack, TextField } from "@mui/material";
import { ChangeEvent, FormEvent, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { useCreatePriceMutation } from "src/api/services/pricesApi";
import { MatchParam } from "src/constants/matches";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import LoadingButton from "src/components/Buttons/LoadingButton";

interface Props {
  onClose: () => void;
}

const PriceInformation = ({ onClose }: Props) => {
  const { productId } = useParams<MatchParam<"PRODUCT_ID">>();
  const [priceName, setPriceName] = useState<string>("");
  const [amount, setAmount] = useState<number>(0);
  const [intervalCount, setIntervalCount] = useState<number>(0);
  const [interval, setInterval] = useState<string>("");
  const [numberOfMonths, setNumberOfMonths] = useState<number>(1);
  const [defaultPrice, setDefaultPrice] = useState<boolean>(false);
  const [defaultSelection, setDefaultSelection] = useState<boolean>(false);
  const [hasTitration, setHasTitration] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [createPrice] = useCreatePriceMutation();
  const errorLog = useSentryLogger("error");

  const handlePriceName = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setPriceName(e.target.value);
  }, []);

  const handleAmount = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setAmount(Number(e.target.value));
  }, []);

  const handleIntervalCount = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setIntervalCount(Number(e.target.value));
    },
    [],
  );

  const handleInterval = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInterval(e.target.value);
  }, []);

  const handleNumberOfMonths = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setNumberOfMonths(Number(e.target.value));
    },
    [],
  );

  const handleDefaultPrice = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setDefaultPrice(e.target.checked);
  }, []);

  const handleDefaultSelection = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setDefaultSelection(e.target.checked);
    },
    [],
  );

  const handleHasTitration = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setHasTitration(e.target.checked);
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      setLoading(true);

      if (productId == null) return;

      try {
        await createPrice({
          amount,
          interval,
          interval_count: intervalCount,
          name: priceName,
          number_of_months: numberOfMonths,
          product_slug: productId,
          default_price: defaultPrice,
          default_selection: defaultSelection,
          has_titration: hasTitration,
        }).unwrap();
      } catch (err) {
        errorLog("Error creating price", err);
      } finally {
        setPriceName("");
        setAmount(0);
        setInterval("");
        setIntervalCount(0);
        setNumberOfMonths(1);
        setDefaultPrice(false);
        setDefaultSelection(false);
        setLoading(false);
        onClose();
      }
    },
    [
      amount,
      createPrice,
      defaultPrice,
      defaultSelection,
      errorLog,
      hasTitration,
      interval,
      intervalCount,
      numberOfMonths,
      onClose,
      priceName,
      productId,
    ],
  );

  return (
    <Stack gap={2} component="form" onSubmit={handleSubmit}>
      <TextField
        label="Name"
        required
        value={priceName}
        onChange={handlePriceName}
      />
      <TextField
        label="Amount ($)"
        required
        type="number"
        value={amount}
        onChange={handleAmount}
      />
      <TextField
        label="Interval count"
        required
        type="number"
        value={intervalCount}
        onChange={handleIntervalCount}
      />
      <TextField
        label="Interval"
        required
        value={interval}
        onChange={handleInterval}
      />
      <TextField
        label="Number of months"
        required
        type="number"
        value={numberOfMonths}
        onChange={handleNumberOfMonths}
      />
      <Stack direction={"row"} justifyContent={"space-between"} gap={2}>
        <FormControlLabel
          labelPlacement="top"
          sx={{
            alignItems: "center",
            gap: 1,
            margin: 0,
            color: "#6C7278",
            "& .MuiFormControlLabel-label": {
              fontSize: "14px",
            },
          }}
          control={
            <Checkbox
              checked={defaultSelection}
              onChange={handleDefaultSelection}
              sx={{
                padding: 0,
                "&.Mui-checked": {
                  color: "#FF8175",
                },
              }}
            />
          }
          label="Default selection"
        />
        <FormControlLabel
          labelPlacement="top"
          sx={{
            alignItems: "center",
            gap: 1,
            margin: 0,
            color: "#6C7278",
            "& .MuiFormControlLabel-label": {
              fontSize: "14px",
            },
          }}
          control={
            <Checkbox
              checked={defaultPrice}
              onChange={handleDefaultPrice}
              sx={{
                padding: 0,
                "&.Mui-checked": {
                  color: "#FF8175",
                },
              }}
            />
          }
          label="Default price"
        />
        <FormControlLabel
          labelPlacement="top"
          sx={{
            alignItems: "center",
            gap: 1,
            margin: 0,
            color: "#6C7278",
            "& .MuiFormControlLabel-label": {
              fontSize: "14px",
            },
          }}
          control={
            <Checkbox
              checked={hasTitration}
              onChange={handleHasTitration}
              sx={{
                padding: 0,
                "&.Mui-checked": {
                  color: "#FF8175",
                },
              }}
            />
          }
          label="Titration attached"
        />
      </Stack>
      <LoadingButton type="submit" loading={loading}>
        Add new price
      </LoadingButton>
    </Stack>
  );
};

export default PriceInformation;
